/*
  Global product + settings state.

  On load, fetches the live catalog from the Worker (GET /api/products, D1-backed)
  and keeps it in React state. Every component — the public menu, the basket and
  the admin dashboard — reads from here, so an admin save (replaceProduct) shows up
  everywhere at once. The catalog is re-fetched whenever the tab regains focus,
  so another open tab picks up admin changes too.

  If the API can't be reached (e.g. `yarn start` without the Worker), the built-in
  menu in src/data/products.js is shown instead, so the site never renders empty.
*/
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { PRODUCTS as STATIC_PRODUCTS } from "../data/products";
import { CONTACT } from "../data/content";
import { getProducts, getSettings } from "../lib/api";

const ProductsContext = createContext(null);

// Built-in menu → the same shape the API returns.
const FALLBACK = STATIC_PRODUCTS.map((p, i) => ({
  id: p.id,
  slug: p.id,
  name: p.name,
  price: Math.min(...p.prices.map((s) => s.price)),
  description: p.blurb,
  image_url: p.image,
  sizes: p.prices.map((s) => ({ size: s.size, price: s.price })),
  quantity: null,
  tags: p.tags,
  net_carbs: p.netCarbs,
  sugar: p.sugar,
  ingredients: p.ingredients,
  sort_order: i,
}));

// The key a basket line uses for a product (slug keeps old baskets valid).
export const cartKeyFor = (p) => p.slug || p.id;

export function ProductsProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [settings, setSettingsState] = useState({ contact_email: CONTACT.email });
  const [source, setSource] = useState("loading"); // loading | live | fallback
  const [error, setError] = useState(null);
  const hasLive = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const list = await getProducts();
      hasLive.current = true;
      setProducts(list);
      setSource("live");
      setError(null);
    } catch (e) {
      // Keep live data on a network blip; otherwise show the built-in menu.
      if (!hasLive.current) {
        setProducts(FALLBACK);
        setSource("fallback");
      }
      setError(e.message || "Could not load products");
    }
    try {
      const s = await getSettings();
      if (s && s.contact_email) setSettingsState((cur) => ({ ...cur, ...s }));
    } catch (e) {
      /* keep the built-in contact email */
    }
  }, []);

  useEffect(() => {
    refresh();
    const onVisible = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  // Swap in the product returned by a successful PUT (no refetch needed).
  const replaceProduct = useCallback((saved) => {
    setProducts((cur) => cur.map((p) => (p.id === saved.id ? saved : p)));
  }, []);

  const setSettings = useCallback((s) => setSettingsState((cur) => ({ ...cur, ...s })), []);

  const value = useMemo(
    () => ({ products, settings, source, error, refresh, replaceProduct, setSettings }),
    [products, settings, source, error, refresh, replaceProduct, setSettings]
  );
  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
}

export const useProducts = () => useContext(ProductsContext);
